import { FC, memo, useEffect, useMemo, useState } from 'react';
import { Empty, Skeleton, notification } from 'antd';
import { CheckCircleTwoTone, RightOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import Blocks from '../Blocks/Blocks';
import blockStyles from '../Blocks/style.module.css';
import SiteMenu from '../SiteMenu/SiteMenu';
import { SipAccountsIcon } from '../SideMenu/icons';
import { TableFooter } from '../TableFooter';
import { LoadingOverlay } from '../shared/Loading';
import { SipAccount } from '../../models/RoboCall/SipAccount';
import {
  getSipAccounts,
  selectSipAccountsSaving,
  selectSipAccounts,
  sipAccountSlice,
  sipAccountsStatus,
} from '../../store/reducers/SIPAccountSlice';
import { useAppDispatch, useAppSelector } from '../../store/store';
import styles from './style.module.css';

const DEFAULT_PAGE_SIZE = 20;
const SKELETON_CARD_COUNT = 6;

type SipAccountsLocationState = {
  skipInitialRefresh?: boolean;
} | null;

const getAccountValue = (
  ...values: Array<string | null | undefined>
): string | undefined =>
  values.find((value): value is string => Boolean(value?.trim()));

const getAccountTitle = (account: SipAccount): string =>
  getAccountValue(
    account.displayName,
    account.name,
    account.accountAor,
    account.server,
  ) ?? '—';

const getAccountSortValue = (account: SipAccount): string =>
  getAccountValue(
    account.displayName,
    account.name,
    account.accountAor,
    account.server,
  ) ?? '';

const sortSipAccounts = (accounts: SipAccount[]): SipAccount[] =>
  [...accounts].sort((left, right) => {
    if (left.default !== right.default) {
      return left.default ? -1 : 1;
    }

    return getAccountSortValue(left).localeCompare(
      getAccountSortValue(right),
      undefined,
      { sensitivity: 'base' },
    );
  });

const SipAccountListSkeleton: FC = () => (
  <div className={styles.skeletonList}>
    {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
      <div key={index} className={styles.skeletonCard}>
        <Skeleton.Avatar active shape="square" size={44} />
        <Skeleton
          active
          title={{ width: '40%' }}
          paragraph={{ rows: 1, width: ['60%'] }}
          className={styles.skeletonText}
        />
      </div>
    ))}
  </div>
);

const SipAccounts: FC = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const status = useAppSelector(sipAccountsStatus);
  const saving = useAppSelector(selectSipAccountsSaving);
  const sipAccounts = useAppSelector(selectSipAccounts);
  const { updateRequestStatus } = sipAccountSlice.actions;
  const [api, contextHolder] = notification.useNotification();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const locationState = location.state as SipAccountsLocationState;
  const skipRefreshFromNavigation = locationState?.skipInitialRefresh === true;
  const [skipInitialRefresh] = useState(
    () => status.requestSuccess !== null || skipRefreshFromNavigation,
  );

  useEffect(() => {
    if (!skipInitialRefresh) {
      dispatch(getSipAccounts());
    }
  }, [dispatch, skipInitialRefresh]);

  useEffect(() => {
    if (skipRefreshFromNavigation) {
      navigate('/sip-accounts', { replace: true, state: null });
    }
  }, [navigate, skipRefreshFromNavigation]);

  useEffect(() => {
    if (status.requestSuccess !== null) {
      api.info({
        message: status.requestSuccess,
        placement: 'bottomRight',
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
      });
      dispatch(updateRequestStatus());
    }
  }, [status.requestSuccess, api, dispatch, updateRequestStatus]);

  const sortedAccounts = useMemo(
    () => sortSipAccounts(sipAccounts),
    [sipAccounts],
  );

  const visibleAccounts = useMemo(() => {
    const pageStart = pageIndex * pageSize;
    return sortedAccounts.slice(pageStart, pageStart + pageSize);
  }, [pageIndex, pageSize, sortedAccounts]);

  const hasNoAccounts = sortedAccounts.length === 0;
  const isListLoading = status.responseLoading || saving;

  useEffect(() => {
    const lastPageIndex = Math.max(
      Math.ceil(sortedAccounts.length / pageSize) - 1,
      0,
    );

    if (pageIndex > lastPageIndex) {
      setPageIndex(lastPageIndex);
    }
  }, [pageIndex, pageSize, sortedAccounts.length]);

  const handlePageChange = (page: number) => {
    setPageIndex(page - 1);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setPageIndex(0);
  };

  return (
    <>
      {contextHolder}
      <Blocks
        topBlock={
          <div className={blockStyles.topBlockRow}>
            <SiteMenu
              title="Настройки: SIP-аккаунты"
              subtitle="Выберите аккаунт для настройки"
              svg={true}
              svgType="sipAccounts"
            />
          </div>
        }
        mainBlock={
          status.responseLoading && sipAccounts.length === 0 ? (
            <SipAccountListSkeleton />
          ) : (
            <LoadingOverlay spinning={isListLoading}>
              <div className={styles.sipAccountsTable}>
                <div className={styles.listScroll}>
                  {hasNoAccounts ? (
                    <div
                      className={styles.emptyCard}
                      data-testid="sip-accounts-empty-state"
                    >
                      <Empty description="Нет данных" />
                    </div>
                  ) : (
                    visibleAccounts.map((account) => {
                      const accountTitle = getAccountTitle(account);
                      const accountAddress =
                        getAccountValue(
                          account.accountAor,
                          account.contactUri,
                          account.server,
                        ) ?? 'Адрес не указан';

                      return (
                        <button
                          key={account.id}
                          type="button"
                          className={styles.accountCard}
                          data-testid={`sip-account-card-${account.id}`}
                          aria-label={`Открыть SIP-аккаунт ${accountTitle}`}
                          disabled={isListLoading}
                          onClick={() =>
                            navigate(`/sip-accounts/${account.id}`)
                          }
                        >
                          <span className={styles.accountIconWrap}>
                            <SipAccountsIcon />
                          </span>

                          <span className={styles.accountInfo}>
                            <span className={styles.accountTitleRow}>
                              <span
                                className={styles.accountName}
                                data-testid="sip-account-name"
                              >
                                {accountTitle}
                              </span>
                              {account.default ? (
                                <span className={styles.defaultBadge}>
                                  По умолчанию
                                </span>
                              ) : null}
                            </span>

                            <span className={styles.accountMeta}>
                              {accountAddress}
                            </span>
                          </span>

                          <span className={styles.arrowIcon} aria-hidden="true">
                            <RightOutlined />
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>

                <TableFooter
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  total={sortedAccounts.length}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  disabled={isListLoading}
                />
              </div>
            </LoadingOverlay>
          )
        }
      />
    </>
  );
});

SipAccounts.displayName = 'SipAccounts';

export default SipAccounts;
